// Installer Growth Pilot — client onboarding wizard (public, noindex).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2, Check, ChevronLeft, ChevronRight, Save, MailCheck, ShieldAlert,
  AlertTriangle, PartyPopper, Clock,
} from "lucide-react";
import SEO from "@/components/SEO";
import { track } from "@/lib/analytics";
import { STEPS, DECLARATIONS, SECURITY_NOTICE, validateStep, validateAll, EMAIL_RE } from "./schema";
import { Field, Repeater, StateRegionsEditor, inputCls } from "./fields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROUTE_PATH = "/installer-growth-pilot/onboarding";

export default function OnboardingPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({});
  const [step, setStep] = useState(0); // index into STEPS
  const [draft, setDraft] = useState(null); // {id, token, expiresAt}
  const [files, setFiles] = useState([]);
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|error
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!!params.get("resume"));
  const [loadError, setLoadError] = useState(""); // "expired" | "invalid"
  const [resumeEmail, setResumeEmail] = useState("");
  const [resumeMsg, setResumeMsg] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {reference, businessName}
  const [submitError, setSubmitError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [regionWarning, setRegionWarning] = useState(false);
  const [proposalModal, setProposalModal] = useState(null); // "trained" | "untrained"
  const [otp, setOtp] = useState({ sent: false, busy: false, error: "", code: "", verified: false, phone: "" });
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const dataRef = useRef(data);
  const stepRef = useRef(step);
  dataRef.current = data;
  stepRef.current = step;

  const submitKey = useMemo(() => crypto.randomUUID(), []);

  // ------------------------------------------------------------- resume ---
  useEffect(() => {
    const token = params.get("resume");
    if (!token) return;
    const [id, tok] = token.split(":");
    (async () => {
      try {
        const r = await axios.get(`${API}/onboarding/drafts/${id}`, { params: { token: tok } });
        setData(r.data.data || {});
        setFiles(r.data.files || []);
        setStep(Math.min(r.data.lastStep || 0, STEPS.length - 1));
        setDraft({ id: r.data.id, token: tok, expiresAt: r.data.expiresAt });
        track("onboarding_resumed");
      } catch (e) {
        setLoadError(e?.response?.status === 410 ? "expired" : "invalid");
      } finally {
        setLoading(false);
        // remove the token from the URL so it doesn't linger in history/shares
        setParams({}, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------ autosave --
  const saveDraft = useCallback(async (d, s) => {
    const email = d?.step1?.email?.trim().toLowerCase();
    try {
      setSaveState("saving");
      if (!draft) {
        if (!email || !EMAIL_RE.test(email)) { setSaveState("idle"); return null; }
        const r = await axios.post(`${API}/onboarding/drafts`, { email, data: d, lastStep: s });
        const nd = { id: r.data.id, token: r.data.token, expiresAt: r.data.expiresAt };
        setDraft(nd);
        setSaveState("saved");
        dirtyRef.current = false;
        track("draft_saved");
        return nd;
      }
      await axios.put(`${API}/onboarding/drafts/${draft.id}`, { data: d, lastStep: s, email }, { params: { token: draft.token } });
      setSaveState("saved");
      dirtyRef.current = false;
      track("draft_saved");
      return draft;
    } catch {
      setSaveState("error");
      return null;
    }
  }, [draft]);

  useEffect(() => {
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveDraft(dataRef.current, stepRef.current), 2500);
    return () => clearTimeout(timerRef.current);
  }, [data, step, saveDraft]);

  // warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current && saveState !== "saved" && !submitted) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveState, submitted]);

  useEffect(() => { track("onboarding_started"); }, []);

  // ------------------------------------------------------------- setters --
  const setField = (stepKey, key, value) => {
    setData((d) => ({ ...d, [stepKey]: { ...(d[stepKey] || {}), [key]: value } }));
    if (stepKey === "step5" && key === "proposalTool") {
      if (["Pylon", "OpenSolar", "GreenSketch"].includes(value)) setProposalModal("trained");
      else if (value === "Other") setProposalModal("untrained");
    }
    setErrors((errs) => {
      if (!errs[key]) return errs;
      const next = { ...errs };
      delete next[key];
      return next;
    });
  };

  const setAreas = (v) => {
    setData((d) => ({ ...d, step3: { ...(d.step3 || {}), states: v.states, regions: v.regions } }));
    setErrors((errs) => {
      const next = { ...errs };
      delete next.states;
      delete next.regions;
      return next;
    });
  };

  // ------------------------------------------------------------ uploads ---
  const uploadProps = {
    files,
    ready: !!draft,
    onUpload: async (file) => {
      if (!draft) throw new Error("Save your draft first.");
      if (file.size > 20 * 1024 * 1024) throw new Error(`${file.name} is over the 20 MB per-file limit.`);
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/onboarding/drafts/${draft.id}/files`, fd, { params: { token: draft.token } });
      setFiles((fs) => [...fs, r.data]);
      return r.data;
    },
    onRemove: async (fileId) => {
      try {
        await axios.delete(`${API}/onboarding/drafts/${draft.id}/files/${fileId}`, { params: { token: draft.token } });
      } catch { /* already gone */ }
      setFiles((fs) => fs.filter((f) => f.id !== fileId));
    },
  };

  // ------------------------------------------------------------ actions ---
  const current = STEPS[step];
  const stepKey = `step${current.id}`;

  const advanceStep = () => {
    track("onboarding_step_completed", { step: current.id });
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    setErrors({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goNext = () => {
    if (current.review) return;
    const errs = validateStep(current.id, data);
    setErrors(errs);
    if (Object.keys(errs).length) {
      track("form_error", { step: current.id });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // Regions nudge — more regions means better campaign reach. Advisory only.
    if (current.id === 3 && (data.step3?.regions || []).length < 2) {
      setRegionWarning(true);
      return;
    }
    advanceStep();
  };

  const goTo = (i) => { setStep(i); setErrors({}); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const emailResumeLink = async () => {
    setEmailMsg("");
    const dr = (await saveDraft(dataRef.current, stepRef.current)) || draft;
    if (!dr) { setEmailMsg("Enter your direct email in Step 1 so we can create your draft first."); return; }
    try {
      await axios.post(`${API}/onboarding/drafts/${dr.id}/send-resume`, null, { params: { token: dr.token } });
      setEmailMsg("Resume link sent — check your inbox. The link expires in 7 days.");
    } catch (e) {
      setEmailMsg(e?.response?.data?.detail || "Couldn't send the email just now — please try again.");
    }
  };

  const requestFreshLink = async (e) => {
    e.preventDefault();
    setResumeMsg("");
    try {
      await axios.post(`${API}/onboarding/resume-request`, { email: resumeEmail.trim().toLowerCase() });
      setResumeMsg("If a draft exists for that email, a fresh resume link is on its way.");
    } catch {
      setResumeMsg("If a draft exists for that email, a fresh resume link is on its way.");
    }
  };

  // ------------------------------------------------------- SMS OTP ---------
  const sendOtp = async () => {
    setOtp((o) => ({ ...o, busy: true, error: "" }));
    const dr = (await saveDraft(dataRef.current, stepRef.current)) || draft;
    if (!dr) {
      setOtp((o) => ({ ...o, busy: false, error: "Save your draft first (check your connection), then try again." }));
      return;
    }
    try {
      const r = await axios.post(`${API}/onboarding/drafts/${dr.id}/otp/send`, null, { params: { token: dr.token } });
      setOtp((o) => ({ ...o, busy: false, sent: true, phone: r.data.phone }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setOtp((o) => ({ ...o, busy: false, error: typeof detail === "string" ? detail : "Couldn't send the SMS — please try again." }));
    }
  };

  const verifyOtp = async () => {
    setOtp((o) => ({ ...o, busy: true, error: "" }));
    try {
      await axios.post(`${API}/onboarding/drafts/${draft.id}/otp/verify`, { code: otp.code }, { params: { token: draft.token } });
      setOtp((o) => ({ ...o, busy: false, verified: true, error: "" }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setOtp((o) => ({ ...o, busy: false, error: typeof detail === "string" ? detail : "Verification failed — try again." }));
    }
  };

  const submit = async () => {
    if (submitting || submitted) return;
    if (!otp.verified) {
      setSubmitError("Please verify the primary contact's mobile with the SMS code before submitting.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const allErrs = validateAll(data);
    if (Object.keys(allErrs).length) {
      const firstStep = Math.min(...Object.keys(allErrs).map(Number));
      setErrors(allErrs[STEPS[step].id] || {});
      setSubmitError("Some required information is missing or invalid. Review the highlighted sections.");
      goTo(STEPS.findIndex((s) => s.id === firstStep));
      setErrors(allErrs[firstStep] || {});
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const dr = (await saveDraft(dataRef.current, stepRef.current)) || draft;
      if (!dr || !dr.id) {
        setSubmitError("We couldn't save your draft — check your connection and try again. Your answers are still on this page.");
        return;
      }
      const r = await axios.post(`${API}/onboarding/drafts/${dr.id}/submit`, {
        token: dr.token, submitKey, data: dataRef.current, website: honeypot,
      });
      setSubmitted({ reference: r.data.reference, businessName: r.data.businessName });
      track("onboarding_submitted");
      window.scrollTo({ top: 0 });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.errors) {
        const byStep = {};
        for (const err of detail.errors) {
          byStep[err.step] = byStep[err.step] || {};
          byStep[err.step][err.field] = err.message;
        }
        const firstStep = Math.min(...Object.keys(byStep).map(Number));
        goTo(STEPS.findIndex((s) => s.id === firstStep));
        setErrors(byStep[firstStep] || {});
        setSubmitError(detail.message);
      } else {
        setSubmitError(typeof detail === "string" ? detail : "Submission failed — your draft is saved. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------- render --
  if (loading) {
    return (
      <div className="min-h-screen bg-soft flex items-center justify-center text-forest">
        <Loader2 className="h-6 w-6 animate-spin mr-3" /> Restoring your saved draft…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-soft text-forest">
        <SEO title="Resume onboarding | Green Circle Group" path={ROUTE_PATH} noindex />
        <div className="mx-auto max-w-lg px-5 pt-24 pb-16 text-center">
          <AlertTriangle className="h-10 w-10 text-yellow-600 mx-auto" />
          <h1 className="mt-4 text-2xl font-extrabold">
            {loadError === "expired" ? "This onboarding link has expired" : "This onboarding link isn't valid"}
          </h1>
          <p className="mt-2 text-mid-neutral">
            Enter the email you used on the form and we'll send you a fresh secure link.
          </p>
          <form onSubmit={requestFreshLink} className="mt-6 space-y-3">
            <input type="email" required value={resumeEmail} onChange={(e) => setResumeEmail(e.target.value)}
              placeholder="you@business.com.au" aria-label="Your email" data-testid="resume-email" className={inputCls} />
            <button type="submit" data-testid="resume-request"
              className="w-full rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold">
              Email me a new link
            </button>
          </form>
          {resumeMsg && <p className="mt-3 text-sm font-semibold text-forest">{resumeMsg}</p>}
          <Link to={ROUTE_PATH} className="mt-6 inline-block text-sm text-mid-neutral underline">Start a new form instead</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-soft text-forest">
        <SEO title="Onboarding submitted | Green Circle Group" path={ROUTE_PATH} noindex />
        <div className="mx-auto max-w-xl px-5 pt-24 pb-16 text-center">
          <PartyPopper className="h-12 w-12 text-mint mx-auto" />
          <h1 className="mt-5 text-3xl font-extrabold" data-testid="onboarding-success">
            Thank you — your onboarding form has been submitted
          </h1>
          <div className="mt-6 rounded-2xl bg-white border border-border-neutral p-6 text-left space-y-2">
            <p className="text-sm"><span className="text-mid-neutral">Reference:</span> <strong data-testid="onboarding-reference">{submitted.reference}</strong></p>
            {submitted.businessName && <p className="text-sm"><span className="text-mid-neutral">Business:</span> <strong>{submitted.businessName}</strong></p>}
            <p className="text-sm text-mid-neutral">A copy has been emailed to you.</p>
            <p className="text-sm text-mid-neutral">
              Green Circle Group will review your information and contact you to finalise your campaign,
              package and sales-process setup. Questions? Email{" "}
              <a className="text-forest font-semibold underline" href="mailto:sam@greencirclegroup.com.au">sam@greencirclegroup.com.au</a>.
            </p>
          </div>
          <Link to="/" data-testid="onboarding-home"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-forest text-white px-6 py-3 text-sm font-bold">
            Return to Green Circle Group
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-soft text-forest">
      <SEO title="Installer Growth Pilot – Client Onboarding | Green Circle Group" path={ROUTE_PATH} noindex
        description="Client onboarding for the Green Circle Group Installer Growth Pilot." />

      {/* slim branded header — distraction-free */}
      <header className="bg-forest text-white">
        <div className="mx-auto max-w-4xl px-5 py-4 flex items-center justify-between">
          <span className="text-sm font-extrabold tracking-wide">Green Circle Group</span>
          <SaveIndicator state={saveState} />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="onboarding-title">
          Installer Growth Pilot – Client Onboarding
        </h1>
        <p className="mt-4 text-mid-neutral leading-relaxed">
          Thank you for choosing to participate in Green Circle Group's Installer Growth Pilot. This quick
          Stage 1 form collects the essentials so we can start preparing your advertising campaigns — our team
          will gather the finer details with you over the phone afterwards.
        </p>
        <p className="mt-3 text-mid-neutral leading-relaxed">
          You may save your progress and return later. Please have your business details, service-area
          preferences, accreditation details and social media links handy before you begin. (Your package
          price list can simply be emailed to us separately.)
        </p>

        <div className="mt-5 rounded-2xl border border-mint/40 bg-mint/5 p-4 flex gap-3 text-sm">
          <Clock className="h-5 w-5 text-mint shrink-0 mt-0.5" />
          <ul className="text-forest space-y-0.5">
            <li>Estimated completion time: about 10 minutes.</li>
            <li>Required fields are marked with an asterisk (<span className="text-red-600">*</span>).</li>
            <li>Only enter details you currently have available — we can collect the rest later.</li>
            <li>You can save a draft and continue later — we'll email you a secure link.</li>
          </ul>
        </div>

        {/* progress */}
        <nav aria-label="Onboarding progress" className="mt-8">
          <ol className="flex flex-wrap gap-1.5">
            {STEPS.map((s, i) => (
              <li key={s.id}>
                <button type="button" onClick={() => goTo(i)} aria-current={i === step ? "step" : undefined}
                  data-testid={`step-tab-${s.id}`}
                  className={"rounded-full px-3 py-1.5 text-[11px] font-bold transition-colors " +
                    (i === step ? "bg-forest text-white" : i < step ? "bg-mint/25 text-forest" : "bg-white text-mid-neutral border border-border-neutral")}>
                  {s.id}. {s.name}
                </button>
              </li>
            ))}
          </ol>
        </nav>

        {/* error summary */}
        {Object.keys(errors).length > 0 && (
          <div role="alert" data-testid="error-summary"
            className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <p className="font-bold">Please fix the following before continuing:</p>
            <ul className="mt-1 list-disc ml-5 space-y-0.5">
              {Object.values(errors).slice(0, 8).map((m, i) => <li key={i}>{m}</li>)}
              {Object.keys(errors).length > 8 && <li>…and {Object.keys(errors).length - 8} more</li>}
            </ul>
          </div>
        )}

        {/* honeypot — hidden from humans */}
        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
          <label>Website <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} /></label>
        </div>

        <section className="mt-6" data-testid={`onboarding-step-${current.id}`}>
          {current.securityNotice && <SecurityNotice />}
          {current.review ? (
            <ReviewStep data={data} files={files} errors={errors} goToStep={goTo}
              setField={setField} stepKey={stepKey}
              otp={otp} setOtp={setOtp} sendOtp={sendOtp} verifyOtp={verifyOtp}
              phone={(data.step1?.phone || "").trim()} />
          ) : (
            (current.sections || []).map((sec) => (
              <fieldset key={sec.title} className="mb-8 rounded-2xl bg-white border border-border-neutral p-5 md:p-6">
                <legend className="text-lg font-extrabold text-forest mb-1">{sec.title}</legend>
                {sec.intro && <p className="mb-3 text-sm text-mid-neutral">{sec.intro}</p>}
                {sec.note && (
                  <p className="mb-3 rounded-xl bg-soft p-3 text-xs text-mid-neutral italic">{sec.note}</p>
                )}
                {sec.areas ? (
                  <>
                    <StateRegionsEditor value={data.step3} onChange={setAreas} errors={errors} />
                    <div className="mt-5 grid gap-4">
                      {(current.extraFields || []).map((f) => (
                        <Field key={f.key} f={{ ...f, half: false }} value={data[stepKey]?.[f.key]} ctx={data[stepKey] || {}}
                          onChange={(v) => setField(stepKey, f.key, v)} error={errors[f.key]} uploadProps={uploadProps} />
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {(sec.fields || []).map((f) => (
                      f.type === "repeater" ? (
                        <Repeater key={f.key} f={f} value={data[stepKey]?.[f.key]} errors={errors} uploadProps={uploadProps}
                          titleOf={(item, i) => item.name || item.type || item.provider || item.area || `Entry ${i + 1}`}
                          onChange={(v) => setField(stepKey, f.key, v)} />
                      ) : (
                        <Field key={f.key} f={f} value={data[stepKey]?.[f.key]} ctx={data[stepKey] || {}}
                          onChange={(v) => setField(stepKey, f.key, v)} error={errors[f.key]} uploadProps={uploadProps} />
                      )
                    ))}
                  </div>
                )}
              </fieldset>
            ))
          )}
        </section>

        {submitError && (
          <p role="alert" data-testid="submit-error" className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">{submitError}</p>
        )}
        {emailMsg && <p className="mb-4 rounded-xl bg-mint/10 border border-mint/40 p-3 text-sm font-semibold text-forest">{emailMsg}</p>}

        {/* controls */}
        <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-border-neutral pt-6">
          <button type="button" onClick={() => goTo(Math.max(0, step - 1))} disabled={step === 0}
            data-testid="onboarding-back"
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-border-neutral text-forest px-4 py-2.5 text-sm font-bold disabled:opacity-40">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <button type="button" onClick={() => saveDraft(dataRef.current, stepRef.current)}
            data-testid="onboarding-save-draft"
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-border-neutral text-forest px-4 py-2.5 text-sm font-bold">
            <Save className="h-4 w-4" /> Save draft
          </button>
          <button type="button" onClick={emailResumeLink}
            data-testid="onboarding-email-link"
            className="inline-flex items-center gap-2 rounded-lg bg-white border border-border-neutral text-forest px-4 py-2.5 text-sm font-bold">
            <MailCheck className="h-4 w-4" /> Save & continue later
          </button>
          <span className="flex-1" />
          {current.review ? (
            <button type="button" onClick={submit} disabled={submitting || !otp.verified}
              data-testid="onboarding-submit"
              title={otp.verified ? undefined : "Verify the primary contact's mobile first"}
              className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-6 py-3 text-sm font-bold disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {submitting ? "Submitting…" : "Submit onboarding"}
            </button>
          ) : (
            <button type="button" onClick={goNext} data-testid="onboarding-next"
              className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-6 py-3 text-sm font-bold">
              {step === STEPS.length - 2 ? "Review submission" : "Save and continue"} <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        <p className="mt-6 text-xs text-mid-neutral">
          This form is protected against spam. See our{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link> for how we handle your information.
        </p>
      </main>

      {/* Region coverage nudge */}
      {regionWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-forest/60 p-4" role="dialog" aria-modal="true"
          data-testid="region-warning-modal" aria-labelledby="region-warning-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 id="region-warning-title" className="text-lg font-extrabold text-forest">Add more regions?</h3>
            <p className="mt-2 text-sm text-mid-neutral">
              You've only selected <strong>{(data.step3?.regions || []).length} region</strong>. The more regions
              you can service, the better your campaigns will perform — wider coverage means more leads and more
              sales. Please double-check you've selected every region you can cover.
            </p>
            <div className="mt-5 flex gap-3">
              <button type="button" onClick={() => setRegionWarning(false)} data-testid="region-warning-add-more"
                className="flex-1 rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold">
                Add more regions
              </button>
              <button type="button" onClick={() => { setRegionWarning(false); advanceStep(); }} data-testid="region-warning-continue"
                className="flex-1 rounded-lg bg-white border border-border-neutral text-forest px-4 py-2.5 text-sm font-bold">
                Continue anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proposal tool feedback */}
      {proposalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-forest/60 p-4" role="dialog" aria-modal="true"
          data-testid="proposal-modal" aria-labelledby="proposal-modal-title">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            {proposalModal === "trained" ? (
              <>
                <h3 id="proposal-modal-title" className="text-lg font-extrabold text-forest">Excellent choice</h3>
                <p className="mt-2 text-sm text-mid-neutral">
                  Great news — our team members are already trained in this application, so we can hit the ground
                  running with your proposals.
                </p>
              </>
            ) : (
              <>
                <h3 id="proposal-modal-title" className="text-lg font-extrabold text-forest">Heads up</h3>
                <p className="mt-2 text-sm text-mid-neutral">
                  We don't have training in that platform yet. No problem — we'll reach out to you later, and we'll
                  ask you to show our team how it works.
                </p>
              </>
            )}
            <button type="button" onClick={() => setProposalModal(null)} data-testid="proposal-modal-ok"
              className="mt-5 w-full rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold">
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SaveIndicator({ state }) {
  const map = {
    idle: null,
    saving: ["Saving…", "text-white/70"],
    saved: ["Saved", "text-lime"],
    error: ["Couldn't save — retrying automatically", "text-yellow-300"],
  };
  const item = map[state];
  if (!item) return <span />;
  return (
    <span data-testid="save-indicator" className={"text-xs font-bold inline-flex items-center gap-1.5 " + item[1]}>
      {state === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
      {state === "saved" && <Check className="h-3 w-3" />}
      {item[0]}
    </span>
  );
}

export function SecurityNotice() {
  return (
    <div data-testid="security-notice" role="note"
      className="mb-6 rounded-2xl border-2 border-yellow-400 bg-yellow-50 p-4 flex gap-3">
      <ShieldAlert className="h-6 w-6 text-yellow-600 shrink-0 mt-0.5" />
      <p className="text-sm text-yellow-900"><strong>{SECURITY_NOTICE.split(":")[0]}:</strong>{SECURITY_NOTICE.split(":").slice(1).join(":")}</p>
    </div>
  );
}

// -------------------------------------------------------------- review -----
function ReviewStep({ data, files, errors, goToStep, setField, stepKey, otp, setOtp, sendOtp, verifyOtp, phone }) {
  const s7 = data.step7 || {};
  const decls = s7.declarations || {};
  return (
    <div>
      <h2 className="text-lg font-extrabold text-forest mb-2">Review your answers</h2>
      <p className="text-sm text-mid-neutral mb-5">
        Check each section before submitting. Anything missing or invalid is highlighted.
      </p>
      {STEPS.filter((s) => !s.review).map((s) => (
        <ReviewSection key={s.id} step={s} data={data[`step${s.id}`] || {}}
          onEdit={() => goToStep(s.id - 1)} />
      ))}

      {/* SMS verification gate */}
      <fieldset className="mt-8 rounded-2xl border-2 border-mint/50 bg-mint/5 p-5" data-testid="otp-panel">
        <legend className="text-lg font-extrabold text-forest px-1">Verify your mobile</legend>
        <p className="text-sm text-mid-neutral">
          Before you submit, we'll SMS a 6-digit verification code to the primary contact's mobile
          {phone ? <> — <strong className="text-forest" data-testid="otp-phone-display">{phone}</strong></> : ""}.
          {phone ? " If that number is wrong, " : " "}
          <button type="button" onClick={() => goToStep(0)} className="underline font-semibold text-forest">edit it in Step 1</button>.
        </p>
        {otp.verified ? (
          <p className="mt-3 inline-flex items-center gap-2 rounded-lg bg-lime px-4 py-2.5 text-sm font-bold text-forest" data-testid="otp-verified">
            <Check className="h-4 w-4" /> Mobile verified — you're ready to submit.
          </p>
        ) : !otp.sent ? (
          <button type="button" onClick={sendOtp} disabled={otp.busy || !phone} data-testid="otp-send"
            className="mt-3 inline-flex items-center gap-2 rounded-lg bg-forest text-white px-5 py-2.5 text-sm font-bold disabled:opacity-50">
            {otp.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {otp.busy ? "Sending…" : "Send code"}
          </button>
        ) : (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label htmlFor="otp-code" className="sr-only">6-digit verification code</label>
            <input id="otp-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              value={otp.code} onChange={(e) => setOtp((o) => ({ ...o, code: e.target.value.replace(/\D/g, "") }))}
              placeholder="6-digit code" data-testid="otp-code"
              className="w-36 p-2.5 rounded-xl bg-white border border-border-neutral focus:border-mint focus:outline-none text-center text-lg font-bold tracking-[0.3em] text-forest" />
            <button type="button" onClick={verifyOtp} disabled={otp.busy || otp.code.length !== 6} data-testid="otp-verify"
              className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-5 py-2.5 text-sm font-bold disabled:opacity-50">
              {otp.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Verify
            </button>
            <button type="button" onClick={sendOtp} disabled={otp.busy} data-testid="otp-resend"
              className="text-xs font-bold text-mid-neutral underline hover:text-forest">Resend code</button>
          </div>
        )}
        {otp.error && <p role="alert" data-testid="otp-error" className="mt-2 text-xs font-semibold text-red-600">{otp.error}</p>}
        {errors.otp && <p role="alert" className="mt-2 text-xs font-semibold text-red-600">{errors.otp}</p>}
      </fieldset>

      <fieldset className="mt-8 rounded-2xl border border-border-neutral bg-white p-5">
        <legend className="text-lg font-extrabold text-forest px-1">Declarations</legend>
        {errors.declarations && <p role="alert" className="mb-2 text-xs font-semibold text-red-600">{errors.declarations}</p>}
        <div className="space-y-3">
          {DECLARATIONS.map(([key, text]) => (
            <label key={key} className="flex items-start gap-2.5 text-sm text-forest cursor-pointer" data-testid={`declaration-${key}`}>
              <input type="checkbox" checked={!!decls[key]} className="mt-0.5 accent-[#03221A] h-4 w-4"
                onChange={(e) => setField("step7", "declarations", { ...decls, [key]: e.target.checked })} />
              <span>{text}</span>
            </label>
          ))}
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field f={{ key: "signerName", label: "Authorised person's full name", required: true, half: true }}
            value={s7.signerName} ctx={s7} onChange={(v) => setField("step7", "signerName", v)} error={errors.signerName} />
          <Field f={{ key: "signerTitle", label: "Position/title", required: true, half: true }}
            value={s7.signerTitle} ctx={s7} onChange={(v) => setField("step7", "signerTitle", v)} error={errors.signerTitle} />
          <Field f={{ key: "signature", label: "Electronic acknowledgement/signature (type your full name)", required: true, half: true }}
            value={s7.signature} ctx={s7} onChange={(v) => setField("step7", "signature", v)} error={errors.signature} />
          <div>
            <label className="block text-sm font-bold text-forest mb-1">Date &amp; time</label>
            <p className="p-2.5 rounded-xl bg-soft text-sm text-mid-neutral">Recorded automatically on submission</p>
          </div>
          <Field f={{ key: "finalComments", label: "Final comments (optional)", type: "textarea" }}
            value={s7.finalComments} ctx={s7} onChange={(v) => setField("step7", "finalComments", v)} />
        </div>
        <p className="mt-4 rounded-xl bg-soft p-3 text-xs text-mid-neutral italic">
          This onboarding submission is for information collection and operational setup. It does not replace the
          parties' signed service agreement.
        </p>
      </fieldset>
    </div>
  );
}

function ReviewSection({ step, data, onEdit }) {
  const summary = summarise(step.id, data);
  return (
    <div className="mb-3 rounded-xl border border-border-neutral bg-white p-4" data-testid={`review-step-${step.id}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-extrabold text-forest">{step.id}. {step.name}</h3>
        <button type="button" onClick={onEdit} data-testid={`review-edit-${step.id}`}
          className="text-xs font-bold text-mint hover:text-forest underline">Edit</button>
      </div>
      <dl className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1">
        {summary.slice(0, 10).map(([k, v]) => (
          <div key={k} className="flex gap-2 text-xs">
            <dt className="text-mid-neutral shrink-0">{k}:</dt>
            <dd className="text-forest font-semibold truncate">{v}</dd>
          </div>
        ))}
        {summary.length === 0 && <p className="text-xs text-mid-neutral italic">No answers yet</p>}
      </dl>
    </div>
  );
}

function summarise(stepId, d) {
  const out = [];
  const push = (k, v) => { if (v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && !v.length)) out.push([k, Array.isArray(v) ? `${v.length} item${v.length === 1 ? "" : "s"}` : String(v)]); };
  if (stepId === 1) {
    push("Contact", `${d.firstName || ""} ${d.lastName || ""}`.trim());
    push("Email", d.email); push("Phone", d.phone);
  } else if (stepId === 2) {
    push("Business", d.tradingName || d.legalName); push("ABN", d.abn); push("Website", d.website);
  } else if (stepId === 3) {
    push("States", Array.isArray(d.states) ? d.states.join(", ") : "");
    const regions = d.regions || [];
    push("Regions", regions.length ? `${regions.length} selected` : "");
  } else if (stepId === 4) {
    push("SAA accredited", d.saaAccredited);
    push("NETCC Approved", d.netccApproved);
  } else if (stepId === 5) {
    push("Facebook", d.fbPageUrl); push("Instagram", d.igUrl); push("Proposal tool", d.proposalTool);
  } else if (stepId === 6) {
    push("Campaign start", d.campaignStart); push("Priority", d.priority);
    push("Daily budget", d.dailyBudget ? `$${d.dailyBudget}/day` : "");
  }
  return out;
}
